import { useQueryClient } from '@tanstack/react-query';

import { createMedication, deleteMedication } from 'api/Medication';
import { createWeightLossAdministration } from 'api/WeightLossAdministration';

import { iaRa } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { invalidateHealthSummaryForPatient } from 'lib/helpers/healthSummaryCache';

export const useMedicationActionMutations = ({ data, patientId, navigate }) => {
  const queryClient = useQueryClient();

  const mutateDeleteMedication = useCustomMutation({
    mutationFn: () =>
      deleteMedication(navigate, {
        medicationId: data?.id,
        version: data?.version,
        patientId,
        fromOverview: true
      }),
    successMessage: 'Medication deleted',
    onSuccess: (deletedData) => {
      const deletedMeds = iaRa(deletedData?.deleted);
      if (!deletedMeds.length) return;

      const deletedMedIds = new Set(deletedMeds.map((m) => m.id));
      const removeMeds = (old) => {
        const meds = old?.medicationHistory?.response?.medications;
        if (!meds) return old;
        return {
          ...old,
          medicationHistory: {
            ...old.medicationHistory,
            response: {
              ...old.medicationHistory.response,
              medications: meds.filter((m) => !deletedMedIds.has(m.id))
            }
          }
        };
      };
      queryClient.setQueriesData(
        { queryKey: ['medication', String(patientId)], exact: false },
        removeMeds
      );

      const linkedWlaIds = new Set(
        deletedMeds.map((m) => m.weight_loss_medication_id).filter(Boolean)
      );
      if (linkedWlaIds.size) {
        queryClient.setQueriesData(
          { queryKey: ['weightLossAdministration', String(patientId)], exact: false },
          (old) => {
            if (!old?.data) return old;
            return { ...old, data: old.data.filter((r) => !linkedWlaIds.has(r.id)) };
          }
        );
      }
      invalidateHealthSummaryForPatient({ queryClient, patientId });
    },
    invalidateQueryKey: [
      ['medication', String(patientId)],
      ['weightLossAdministration', String(patientId)]
    ]
  });

  const upsertFormFunctionMutation = useCustomMutation({
    mutationFn: (data) => createMedication(navigate, data),
    onSuccess: (upsertData) => {
      const updatedMeds = iaRa(upsertData?.newResponse?.response?.medications);
      if (updatedMeds.length) {
        const updatedMap = new Map(updatedMeds.map((m) => [m.id, m]));
        const mergeMeds = (old) => {
          const meds = old?.medicationHistory?.response?.medications;
          if (!meds) return old;
          const merged = meds.map((m) => (updatedMap.has(m.id) ? updatedMap.get(m.id) : m));
          const existingIds = new Set(meds.map((m) => m.id));
          const brandNew = updatedMeds.filter((m) => !existingIds.has(m.id));
          return {
            ...old,
            medicationHistory: {
              ...old.medicationHistory,
              response: {
                ...old.medicationHistory.response,
                medications: [...merged, ...brandNew]
              }
            }
          };
        };
        queryClient.setQueriesData(
          { queryKey: ['medication', String(patientId)], exact: false },
          mergeMeds
        );
      }
      invalidateHealthSummaryForPatient({ queryClient, patientId });
    },
    invalidateQueryKey: [['medication', String(patientId)]]
  });

  const upsertWeightLossMutation = useCustomMutation({
    mutationFn: (params) => createWeightLossAdministration(navigate, params),
    onSuccess: (upsertData) => {
      const updatedRows = iaRa(upsertData?.newResponse?.response?.weightLossAdministration);
      if (updatedRows.length) {
        const updatedMap = new Map(updatedRows.map((r) => [r.id, r]));
        queryClient.setQueriesData(
          { queryKey: ['weightLossAdministration', String(patientId)], exact: false },
          (old) => {
            if (!old?.data) return old;
            const merged = old.data.map((r) => (updatedMap.has(r.id) ? updatedMap.get(r.id) : r));
            const existingIds = new Set(old.data.map((r) => r.id));
            const brandNew = updatedRows.filter((r) => !existingIds.has(r.id));
            return { ...old, data: [...merged, ...brandNew] };
          }
        );
      }
      invalidateHealthSummaryForPatient({ queryClient, patientId });
    },
    invalidateQueryKey: [
      ['weightLossAdministration', String(patientId)],
      ['medication', String(patientId)]
    ]
  });

  return { mutateDeleteMedication, upsertFormFunctionMutation, upsertWeightLossMutation };
};
